import React from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import Button from '@components/atoms/button/button';
import { isProductSoldByAProfessionalSeller } from '@helpers/utils/products';
import { scrollToLazy } from '@helpers/utils/scroll-lazy';
import Icon from '@components/atoms/icon/icon';
import { useContextSelector } from 'use-context-selector';
import { ModalContext } from '@context/modal.context';
import { usePreferences } from '@context/preferences.context';
import dynamic from 'next/dynamic';
import useProductQuery from '@hooks/product/use-product-query';
import useUser from '@hooks/user/use-user';
import { useIntersectionObserver } from 'usehooks-ts';
import ProductDescriptionList from './product-description-list/product-description-list';
import styles from './product-description.module.scss';

const ProductSellerDescription = dynamic(() => import('./product-seller-description/product-seller-description'), {
  ssr: false,
});

const ProductReassurance = dynamic(
  () => import('@components/molecules/products/product-description/product-reassurance/product-reassurance'),
  {
    ssr: false,
  },
);

const ProductSellerBlock = dynamic(() => import('./product-seller-block/product-seller-block'), {
  ssr: false,
});

const LegalWarrantiesModal = dynamic(() => import('./legal-warranties-modal/legal-warranties-modal'), {
  ssr: false,
});

interface ProductDescriptionProps {
  canReportProducts?: boolean;
}

const ProductDescription: React.FC<ProductDescriptionProps> = (props) => {
  const { canReportProducts = false } = props;

  const { t } = useTranslation();
  const { locale } = usePreferences();
  const { product } = useProductQuery();
  const { isAuthenticated } = useUser();
  const openModal = useContextSelector(ModalContext, (v) => v.openModal);

  const isFrench: boolean = locale === 'fr-FR';

  const containerRef = React.useRef<HTMLDivElement>(null);
  const entry = useIntersectionObserver(containerRef, {
    freezeOnceVisible: true,
    threshold: 0.5,
    rootMargin: '0px 0px 200px 0px',
  });

  const shouldRenderSellerBlock = !!entry?.isIntersecting;
  const shouldRenderProductReassurance = !!entry?.isIntersecting;

  const openLegalWarrantiesModal = () =>
    openModal((modalDialogTitleId) => <LegalWarrantiesModal modalDialogTitleId={modalDialogTitleId} />, {
      styles: {
        content: {
          maxWidth: '600px',
          width: '100%',
        },
      },
    });

  const scrollToCommentsAndFocus = () => {
    scrollToLazy(
      '#comments',
      // Authenticated users: focus textarea for immediate typing
      isAuthenticated ? { focusSelector: '[data-cy="product-discussion-form-textarea"]' } : undefined, // Unauthenticated: defaults to #comments section
    );
  };

  return (
    <>
      <div
        ref={containerRef}
        className={clsx(styles.description, 'innerContainer')}
      >
        <h2 className={clsx('vc-heading-A24 vc-heading-A40--md-up', styles.description__title)}>
          {t('PRODUCT.DESCRIPTION.TITLE')}
        </h2>
        <div className={styles.description__container}>
          <div className={clsx(styles.description__container__seller)}>
            {shouldRenderSellerBlock && (
              <>
                <h3 className={styles.description__container__column__title}>
                  <span>{t('PRODUCT.DESCRIPTION.SELLER.TITLE')}</span>
                  {isProductSoldByAProfessionalSeller(product) && (
                    <span
                      className={styles.description__container__column__title__pro}
                      data-cy="profile-user-professional-segment"
                    >
                      {t('PRODUCT.DESCRIPTION.SELLER.PR0FESSIONAL')}
                    </span>
                  )}
                </h3>
                <ProductSellerBlock />
              </>
            )}
            <div className={styles.description__legal}>
              <span>
                {t(
                  isProductSoldByAProfessionalSeller(product)
                    ? 'PRODUCT.DESCRIPTION.REASSURANCE.LEGAL_PRO'
                    : 'PRODUCT.DESCRIPTION.REASSURANCE.LEGAL_PRIVATE',
                )}
              </span>
            </div>
          </div>
          <div className={styles.description__container__core}>
            <h3 className={styles.description__container__column__title}>
              <span>{t('PRODUCT.DESCRIPTION.DESCRIPTION.TITLE')}</span>
            </h3>
            <ProductSellerDescription product={product} />
            <h3 className={styles.description__container__column__title}>
              <span>{t('PRODUCT.DESCRIPTION.DETAILS.TITLE')}</span>
            </h3>
            <ProductDescriptionList />
            <Button
              disableDefaultStyling
              onClick={scrollToCommentsAndFocus}
              type="submit"
              className={styles.description__discussionsBtn}
              data-cy="product-description-scroll-to-comments-button"
            >
              <Icon
                className={styles.description__discussionsIcon}
                name="comment-large" // New Icon
                ariaHidden
              />
              <span className={styles.description__discussionsText}>
                {t('PRODUCT.DESCRIPTION.SEE_DISCUSSIONS_BTN')}
              </span>
            </Button>
          </div>
          <div className={styles.description__container__reassurance}>
            <h3 className={styles.description__container__column__title}>
              <span>{t('PRODUCT.DESCRIPTION.MORE_INFO.TITLE')}</span>
            </h3>
            {shouldRenderProductReassurance && <ProductReassurance />}
            {isFrench && (
              <Button
                type="submit"
                disableDefaultStyling
                className={clsx(styles.description__legalWarrantiesLink, 'vc-link')}
                onClick={openLegalWarrantiesModal}
                data-cy="product-description-open-legal-warranties-modal-button"
              >
                Garanties légales
              </Button>
            )}
            {canReportProducts && (
              <Button
                disableDefaultStyling
                type="submit"
                className={clsx(styles.description__reportLink, 'vc-link')}
                data-cy="product-description-report-product-button"
              >
                {t('PRODUCT.DESCRIPTION.REASSURANCE.REPORT')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductDescription;
